import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { SlidersHorizontal, X } from 'lucide-react'
import { adminUsersApi, rolesApi } from '../../lib/api'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { useAuth } from '../../hooks/useAuth'
import PermissionMatrix from '../../components/shared/PermissionMatrix'
import type { AdminUser } from '../../types'
import { tx } from '../../lib/i18n'

const errorOf = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback

/**
 * Personal permissions of one admin: starts from the role, tick/untick to add or remove
 * single permissions for this person only (saved as allow / deny lists).
 */
const UserPermissionsModal: React.FC<{ user: AdminUser; onClose: () => void }> = ({ user, onClose }) => {
  const qc = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()
  const { currentAdmin } = useAuth()

  const { data: catalog } = useQuery({
    queryKey: ['permission-catalog'],
    queryFn: () => rolesApi.catalog().then((r) => r.data.data),
    staleTime: Infinity,
  })
  const { data: info, isLoading } = useQuery({
    queryKey: ['admin-user-permissions', user.id],
    queryFn: () => adminUsersApi.getPermissions(user.id).then((r) => r.data.data),
  })

  const base = useMemo(() => new Set(info?.rolePermissions ?? []), [info])
  const [draft, setDraft] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (info) setDraft(new Set(info.effective))
  }, [info])

  const allow = [...draft].filter((p) => !base.has(p))
  const deny = [...base].filter((p) => !draft.has(p))
  const saved = new Set(info?.effective ?? [])
  const dirty = draft.size !== saved.size || [...draft].some((p) => !saved.has(p))
  const self = currentAdmin?.id === user.id && currentAdmin.role !== 'super_admin'
  const readOnly = !can('admin-users.permissions') || self

  const save = useMutation({
    mutationFn: () => adminUsersApi.setPermissions(user.id, { allow, deny }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-user-permissions', user.id] })
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      showToast({ type: 'success', message: tx('Personal permissions saved.') })
      onClose()
    },
    onError: (e) => showToast({ type: 'error', message: errorOf(e, tx('Failed to save permissions.')) }),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="user-permissions-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-amber-600" /> {tx('Personal permissions')} — {user.firstName} {user.lastName}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {tx('Role')}: <b>{info?.roleLabel ?? user.role}</b> · {tx('Tick to add a permission for this person only, untick to take one away.')}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg" aria-label="close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading || !catalog || !info ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
            </div>
          ) : (
            <>
              {self && <p className="mb-3 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">{tx('You cannot change your own permissions.')}</p>}
              <PermissionMatrix resources={catalog.resources} value={draft} onChange={setDraft} base={base} readOnly={readOnly} testIdPrefix="user" />
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-gray-500" data-testid="user-perm-diff">
            {allow.length > 0 && <span className="text-green-700 mr-2">+{allow.length} {tx('added for this person')}</span>}
            {deny.length > 0 && <span className="text-red-700 mr-2">−{deny.length} {tx('removed for this person')}</span>}
            {!allow.length && !deny.length && tx('Same as the role')}
          </div>
          <div className="flex gap-2">
            {!readOnly && (allow.length > 0 || deny.length > 0) && (
              <button onClick={() => setDraft(new Set(base))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="user-perm-reset">
                {tx('Back to role permissions')}
              </button>
            )}
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="user-perm-cancel">
              {tx('Cancel')}
            </button>
            {!readOnly && (
              <button
                onClick={() => save.mutate()}
                disabled={!dirty || save.isPending}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                data-testid="user-perm-save"
              >
                {save.isPending ? tx('Saving...') : tx('Save')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserPermissionsModal
